import { memo, useContext, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { CopilotContext } from '@Sage/ssm-web5';

const EntityCrudContent = ({
    children,
    bottomSpacing = 0,
    includeCopilotInBottomInset = true,
    onScrollbarWidthChange,
    className,
    style,
    ...restProps
}) => {
    const { copilotBarProps } = useContext(CopilotContext);
    const {
        height: copilotBarHeight,
        isMinimized: isCopilotBarMinimized,
        isOpen: isCopilotBarOpen,
        padding: copilotBarPadding,
    } = copilotBarProps;

    const containerRef = useRef(null);
    const previousBottomInsetRef = useRef(null);

    const copilotBottomInset =
        !isCopilotBarOpen || isCopilotBarMinimized ? 0 : copilotBarPadding * 2 + copilotBarHeight;

    const copilotPart = includeCopilotInBottomInset ? copilotBottomInset : 0;
    const totalBottomInset = copilotPart + bottomSpacing;
    const shouldApplyBottomInset = totalBottomInset > 0;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const previousBottomInset = previousBottomInsetRef.current;
        const currentBottomInset = shouldApplyBottomInset ? totalBottomInset : 0;

        if (previousBottomInset !== null && previousBottomInset !== currentBottomInset) {
            const { scrollHeight, scrollTop, clientHeight } = container;
            const wasAtBottom = scrollHeight - scrollTop - clientHeight < 10;

            if (wasAtBottom) {
                requestAnimationFrame(() => {
                    if (container) {
                        container.scrollTop = container.scrollHeight;
                    }
                });
            }
        }

        previousBottomInsetRef.current = currentBottomInset;
    }, [shouldApplyBottomInset, totalBottomInset]);

    useLayoutEffect(() => {
        if (!onScrollbarWidthChange) return undefined;

        const container = containerRef.current;
        if (!container) return undefined;

        const updateScrollbarWidth = () => {
            onScrollbarWidthChange(Math.max(container.offsetWidth - container.clientWidth, 0));
        };

        updateScrollbarWidth();

        const resizeObserver = new ResizeObserver(updateScrollbarWidth);
        resizeObserver.observe(container);

        const contentEl = container.firstElementChild;
        if (contentEl) resizeObserver.observe(contentEl);

        return () => resizeObserver.disconnect();
        // totalBottomInset is included so the scrollbar reading refreshes when the spacer height
        // changes (RO does not fire on inner scrollHeight changes alone).
    }, [onScrollbarWidthChange, totalBottomInset]);

    // Real spacer node — see EntityDetailContent for rationale (scrollHeight + overflowing children).
    const spacerStyle = useMemo(
        () => ({ height: totalBottomInset, flex: '0 0 auto' }),
        [totalBottomInset],
    );

    return (
        <div
            {...restProps}
            ref={containerRef}
            className={classNames('entity-crud-content', className)}
            style={style}
        >
            {children}
            {shouldApplyBottomInset && (
                <div
                    aria-hidden="true"
                    className="entity-crud-content__bottom-spacer"
                    style={spacerStyle}
                />
            )}
        </div>
    );
};

export default memo(EntityCrudContent);
